/**
 * Date utility functions for BuildHub
 *
 * These functions handle date conversion between the frontend and backend,
 * ensuring that date-only values (without time) are properly formatted
 * without timezone conversion issues.
 */

/**
 * Formats a Date object to YYYY-MM-DD string in the user's local timezone
 *
 * This function extracts the year, month, and day from the Date object
 * in the user's local timezone and formats them without any timezone conversion.
 *
 * @param date - The Date object to format
 * @returns A string in YYYY-MM-DD format, or null if date is null
 *
 * @example
 * // User in Auckland (UTC+13) selects January 1st, 2025
 * const date = new Date(2025, 0, 1); // Local: 2025-01-01T00:00:00+13:00
 * formatDateToYYYYMMDD(date); // Returns: "2025-01-01"
 */
export function formatDateToYYYYMMDD(
    date: Date | null | undefined
): string | null {
    if (!date) return null;

    // Get the year, month, and day in the user's local timezone
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
}

/**
 * Parses a YYYY-MM-DD date string to a Date object in the user's local timezone
 *
 * This function creates a Date object set to midnight in the user's local timezone,
 * avoiding timezone conversion issues that occur with `new Date(string)`.
 *
 * @param dateString - A date string in YYYY-MM-DD format
 * @returns A Date object at midnight in local timezone, or null if invalid
 *
 * @example
 * // User in Auckland (UTC+13), backend returns "2025-01-01"
 * parseDateFromYYYYMMDD("2025-01-01"); // Returns: Date representing 2025-01-01T00:00:00+13:00
 */
export function parseDateFromYYYYMMDD(
    dateString: string | null | undefined
): Date | null {
    if (!dateString) return null;

    try {
        // Parse the date components
        // Handle "YYYY-MM-DD", "YYYY-MM-DDTHH:MM:SS", or "YYYY-MM-DD HH:MM:SS"
        const afterTSplit = dateString.split('T')[0];
        if (!afterTSplit) return null;
        const datePart = afterTSplit.split(' ')[0];
        if (!datePart) return null;

        const parts = datePart.split('-');
        if (parts.length !== 3) return null;

        const yearStr = parts[0];
        const monthStr = parts[1];
        const dayStr = parts[2];

        if (!yearStr || !monthStr || !dayStr) return null;

        const year = parseInt(yearStr, 10);
        const month = parseInt(monthStr, 10) - 1; // JavaScript months are 0-indexed
        const day = parseInt(dayStr, 10);

        // Check if the parsed values are valid
        if (isNaN(year) || isNaN(month) || isNaN(day)) return null;

        // Create a date object in the user's local timezone
        const date = new Date(year, month, day);

        // Verify the date is valid
        if (isNaN(date.getTime())) return null;

        return date;
    } catch {
        return null;
    }
}

/**
 * Converts a Date object to a date-only string for API submission
 *
 * Alias for formatDateToYYYYMMDD to maintain consistency with naming conventions.
 *
 * @param date - The Date object to format
 * @returns A string in YYYY-MM-DD format, or undefined if date is null
 */
export function formatDateForApi(
    date: Date | null | undefined
): string | undefined {
    const formatted = formatDateToYYYYMMDD(date);
    return formatted ?? undefined;
}
